import * as THREE from 'three';
import { createInteriorGeometry, sanitizeVehicleInteriors } from './vehicleInteriors.js';
import { VEHICLE_EQUIPMENT_SLOTS } from './vehicleEquipment.js';

export const VEHICLE_PROFILE_SCHEMA = 'toybox-vehicle-profile';
export const VEHICLE_PROFILE_VERSION = 3;
export const DEFAULT_IMPORTED_VEHICLE_VISUAL_SCALE = .56;

const BODY_MATERIAL_STATE = new WeakMap();
const BODY_FINISH_UNIFORMS = new WeakMap();
const SPECIAL_FINISH_MATERIAL_PATTERN = /(glass|window|windshield|vidrio|crystal|lens|light|lamp|headlight|taillight|farol|rubber|tire|tyre|neumatic|goma)/i;

const finite = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const clamp = (value, minimum, maximum, fallback = minimum) => (
  THREE.MathUtils.clamp(finite(value, fallback), minimum, maximum)
);

function copyVector(source, fallback) {
  return {
    x: finite(source?.x, fallback.x),
    y: finite(source?.y, fallback.y),
    z: finite(source?.z, fallback.z),
  };
}

function clampVector(source, fallback, limits) {
  return {
    x: clamp(source?.x, limits.x[0], limits.x[1], fallback.x),
    y: clamp(source?.y, limits.y[0], limits.y[1], fallback.y),
    z: clamp(source?.z, limits.z[0], limits.z[1], fallback.z),
  };
}

export function createVehicleBodyFinish() {
  return {
    enabled: true,
    cleanPbrMaps: true,
    metalness: .03,
    roughness: .34,
    clearcoat: .78,
    clearcoatRoughness: .18,
    envMapIntensity: 1,
    saturation: 10,
    brightness: 4,
  };
}

function sanitizeBodyFinish(source = {}) {
  const fallback = createVehicleBodyFinish();
  return {
    enabled: source.enabled !== false,
    cleanPbrMaps: source.cleanPbrMaps !== false,
    metalness: clamp(source.metalness, 0, .35, fallback.metalness),
    roughness: clamp(source.roughness, .08, 1, fallback.roughness),
    clearcoat: clamp(source.clearcoat, 0, 1, fallback.clearcoat),
    clearcoatRoughness: clamp(source.clearcoatRoughness, .02, 1, fallback.clearcoatRoughness),
    envMapIntensity: clamp(source.envMapIntensity, 0, 2.5, fallback.envMapIntensity),
    saturation: clamp(source.saturation, -30, 50, fallback.saturation),
    brightness: clamp(source.brightness, -20, 30, fallback.brightness),
  };
}

export function createVehicleProfile(name = 'vehiculo-personalizado') {
  return {
    schema: VEHICLE_PROFILE_SCHEMA,
    version: VEHICLE_PROFILE_VERSION,
    name,
    assets: {
      bodyFile: '',
      wheelFile: '',
    },
    game:{
      defaultWheelId:'chrome_classic',
      defaultEngineSoundId:'muscle_rumble',
      defaultCannonId:'',
      defaultProjectileId:'',
      defaultNitroId:'',
      defaultExplosionId:'',
      defaultTombstoneId:'',
    },
    body: {
      scale: 1,
      offset: { x: 0, y: 0, z: 0 },
      rotationDegrees: { x: 0, y: 0, z: 0 },
      normalization: null,
      finish: createVehicleBodyFinish(),
      interiors: sanitizeVehicleInteriors(),
    },
    wheels: {
      flipLeft: true,
      normalization: null,
      front: {
        axleZ: -1.30,
        centerY: .52,
        track: 2.08,
        radius: .52,
        width: .28,
      },
      rear: {
        axleZ: 1.30,
        centerY: .52,
        track: 2.08,
        radius: .52,
        width: .28,
      },
    },
    collider: {
      size: { x: 1.72, y: .62, z: 3.45 },
      offset: { x: 0, y: .34, z: 0 },
    },
    sockets: {
      camera: { x: 0, y: 1.12, z: 1.15 },
      weapon: { x: 0, y: .74, z: -1.62 },
      nitro: { x: 0, y: .52, z: 1.72 },
    },
  };
}

function sanitizeAxle(source, fallback, front) {
  return {
    axleZ: clamp(source?.axleZ, front ? -4 : .05, front ? -.05 : 4, fallback.axleZ),
    centerY: clamp(source?.centerY, .1, 3, fallback.centerY),
    track: clamp(source?.track, .8, 4, fallback.track),
    radius: clamp(source?.radius, .15, 1.5, fallback.radius),
    width: clamp(source?.width, .08, 1.2, fallback.width),
  };
}

export function sanitizeVehicleProfile(source = {}) {
  const fallback = createVehicleProfile();
  const body = source.body || {};
  const wheels = source.wheels || {};
  const profile = createVehicleProfile(String(source.name || fallback.name));
  profile.assets.bodyFile = String(source.assets?.bodyFile || '');
  profile.assets.wheelFile = String(source.assets?.wheelFile || '');
  for (const { field } of VEHICLE_EQUIPMENT_SLOTS) {
    profile.game[field] = String(source.game?.[field] || fallback.game[field]);
  }
  profile.body.scale = clamp(body.scale, .05, 4, fallback.body.scale);
  profile.body.offset = clampVector(body.offset, fallback.body.offset, {
    x:[-2,2],
    y:[-1.5,3],
    z:[-4,4],
  });
  profile.body.rotationDegrees = copyVector(body.rotationDegrees, fallback.body.rotationDegrees);
  profile.body.normalization = body.normalization || null;
  profile.body.finish = sanitizeBodyFinish(body.finish);
  profile.body.interiors = sanitizeVehicleInteriors(body.interiors);
  profile.wheels.flipLeft = wheels.flipLeft !== false;
  profile.wheels.normalization = wheels.normalization || null;
  profile.wheels.front = sanitizeAxle(wheels.front, fallback.wheels.front, true);
  profile.wheels.rear = sanitizeAxle(wheels.rear, fallback.wheels.rear, false);
  profile.collider.size = clampVector(source.collider?.size, fallback.collider.size, {
    x:[.5,4],
    y:[.2,3],
    z:[1,8],
  });
  profile.collider.offset = clampVector(source.collider?.offset, fallback.collider.offset, {
    x:[-2,2],
    y:[0,3],
    z:[-4,4],
  });
  const socketLimits={x:[-2,2],y:[0,3],z:[-4,4]};
  profile.sockets.camera = clampVector(source.sockets?.camera, fallback.sockets.camera, socketLimits);
  profile.sockets.weapon = clampVector(source.sockets?.weapon, fallback.sockets.weapon, socketLimits);
  profile.sockets.nitro = clampVector(source.sockets?.nitro, fallback.sockets.nitro, socketLimits);
  return profile;
}

function objectBounds(object) {
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  if (box.isEmpty()) throw new Error('El archivo no contiene geometría visible.');
  return { box, size: box.getSize(new THREE.Vector3()), center: box.getCenter(new THREE.Vector3()) };
}

function copyMaterialSource(source) {
  const color = source.color?.clone?.() || new THREE.Color(0xffffff);
  const emissive = source.emissive?.clone?.() || new THREE.Color(0x000000);
  const normalScale = source.normalScale?.clone?.() || new THREE.Vector2(1, 1);
  return {
    color,
    emissive,
    emissiveIntensity: finite(source.emissiveIntensity, 1),
    map: source.map || null,
    alphaMap: source.alphaMap || null,
    emissiveMap: source.emissiveMap || null,
    aoMap: source.aoMap || null,
    normalMap: source.normalMap || null,
    bumpMap: source.bumpMap || null,
    displacementMap: source.displacementMap || null,
    roughnessMap: source.roughnessMap || null,
    metalnessMap: source.metalnessMap || null,
    normalScale,
    aoMapIntensity: finite(source.aoMapIntensity, 1),
    bumpScale: finite(source.bumpScale, 1),
    displacementScale: finite(source.displacementScale, 1),
    displacementBias: finite(source.displacementBias, 0),
    metalness: finite(source.metalness, 0),
    roughness: finite(source.roughness, .82),
    clearcoat: finite(source.clearcoat, 0),
    clearcoatRoughness: finite(source.clearcoatRoughness, .1),
    envMapIntensity: finite(source.envMapIntensity, 1),
    ior: finite(source.ior, 1.5),
    transmission: finite(source.transmission, 0),
    thickness: finite(source.thickness, 0),
    protected: SPECIAL_FINISH_MATERIAL_PATTERN.test(source.name || '')
      || finite(source.transmission, 0) > .01
      || (source.transparent && finite(source.opacity, 1) < .98),
    signature: '',
  };
}

function installBodyFinishShader(material) {
  const uniforms = {
    saturation: { value: 1 },
    brightness: { value: 1 },
  };
  BODY_FINISH_UNIFORMS.set(material, uniforms);
  material.onBeforeCompile = (shader) => {
    shader.uniforms.carscapeBodySaturation = uniforms.saturation;
    shader.uniforms.carscapeBodyBrightness = uniforms.brightness;
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <opaque_fragment>',
      `
        float carscapeBodyLuma = dot(outgoingLight, vec3(0.2126, 0.7152, 0.0722));
        outgoingLight = mix(vec3(carscapeBodyLuma), outgoingLight, carscapeBodySaturation);
        outgoingLight *= carscapeBodyBrightness;
        #include <opaque_fragment>
      `,
    );
    shader.fragmentShader = shader.fragmentShader.replace(
      'void main() {',
      `
        uniform float carscapeBodySaturation;
        uniform float carscapeBodyBrightness;
        void main() {
      `,
    );
  };
  material.customProgramCacheKey = () => 'carscape-vehicle-body-finish-v1';
}

function createBodyMaterial(source) {
  const state = copyMaterialSource(source);
  const material = new THREE.MeshPhysicalMaterial({
    color: state.color,
    emissive: state.emissive,
    emissiveIntensity: state.emissiveIntensity,
    map: state.map,
    alphaMap: state.alphaMap,
    emissiveMap: state.emissiveMap,
    aoMap: state.aoMap,
    normalMap: state.normalMap,
    bumpMap: state.bumpMap,
    displacementMap: state.displacementMap,
    roughnessMap: state.roughnessMap,
    metalnessMap: state.metalnessMap,
    normalScale: state.normalScale,
    aoMapIntensity: state.aoMapIntensity,
    bumpScale: state.bumpScale,
    displacementScale: state.displacementScale,
    displacementBias: state.displacementBias,
    metalness: state.metalness,
    roughness: state.roughness,
    clearcoat: state.clearcoat,
    clearcoatRoughness: state.clearcoatRoughness,
    envMapIntensity: state.envMapIntensity,
    ior: state.ior,
    transmission: state.transmission,
    thickness: state.thickness,
    opacity: finite(source.opacity, 1),
    transparent: Boolean(source.transparent),
    alphaTest: finite(source.alphaTest, 0),
    side: source.side,
    shadowSide: source.shadowSide,
    depthTest: source.depthTest,
    depthWrite: source.depthWrite,
    colorWrite: source.colorWrite,
    vertexColors: Boolean(source.vertexColors),
    flatShading: Boolean(source.flatShading),
    premultipliedAlpha: Boolean(source.premultipliedAlpha),
    dithering: Boolean(source.dithering),
    toneMapped: source.toneMapped !== false,
  });
  material.name = source.name || '';
  material.userData = { ...source.userData };
  BODY_MATERIAL_STATE.set(material, state);
  installBodyFinishShader(material);
  return material;
}

export function prepareVehicleFinishMaterials(object) {
  const replacements = new Map();
  object.traverse((child) => {
    if (!child.isMesh) return;
    const replace = (source) => {
      if (!source) return source;
      if (!replacements.has(source)) replacements.set(source, createBodyMaterial(source));
      return replacements.get(source);
    };
    child.material = Array.isArray(child.material)
      ? child.material.map(replace)
      : replace(child.material);
  });
}

export function applyVehicleBodyFinish(object, sourceFinish = {}) {
  if (!object) return;
  const finish = sanitizeBodyFinish(sourceFinish);
  object.traverse((child) => {
    if (!child.isMesh) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.filter(Boolean).forEach((material) => {
      const state = BODY_MATERIAL_STATE.get(material);
      if (!state) return;
      const enabled = finish.enabled && !state.protected;
      const cleanPbrMaps = enabled && finish.cleanPbrMaps;
      const uniforms = BODY_FINISH_UNIFORMS.get(material);
      if (uniforms) {
        uniforms.saturation.value = enabled ? 1 + finish.saturation / 100 : 1;
        uniforms.brightness.value = enabled ? 1 + finish.brightness / 100 : 1;
      }
      material.color.copy(state.color);
      material.emissive.copy(state.emissive);
      material.emissiveIntensity = state.emissiveIntensity;
      material.metalness = enabled ? finish.metalness : state.metalness;
      material.roughness = enabled ? finish.roughness : state.roughness;
      material.clearcoat = enabled ? finish.clearcoat : state.clearcoat;
      material.clearcoatRoughness = enabled ? finish.clearcoatRoughness : state.clearcoatRoughness;
      material.envMapIntensity = enabled ? finish.envMapIntensity : state.envMapIntensity;
      material.ior = state.ior;
      material.aoMap = cleanPbrMaps ? null : state.aoMap;
      material.normalMap = cleanPbrMaps ? null : state.normalMap;
      material.bumpMap = cleanPbrMaps ? null : state.bumpMap;
      material.displacementMap = cleanPbrMaps ? null : state.displacementMap;
      material.roughnessMap = cleanPbrMaps ? null : state.roughnessMap;
      material.metalnessMap = cleanPbrMaps ? null : state.metalnessMap;
      material.normalScale.copy(state.normalScale);
      material.aoMapIntensity = state.aoMapIntensity;
      material.bumpScale = state.bumpScale;
      material.displacementScale = state.displacementScale;
      material.displacementBias = state.displacementBias;
      const signature = `${enabled}:${cleanPbrMaps}:${material.clearcoat > 0}`;
      if (signature !== state.signature) {
        state.signature = signature;
        material.needsUpdate = true;
      }
    });
  });
}

function applyMaterialDefaults(object) {
  object.traverse((child) => {
    if (!child.isMesh) return;
    child.castShadow = true;
    child.receiveShadow = true;
    child.frustumCulled = true;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.filter(Boolean).forEach((material) => {
      if ('transparent' in material && material.opacity >= .995) material.transparent = false;
      material.needsUpdate = true;
    });
  });
}

export function normalizeBodySource(object, targetLength = 4.4, finish = createVehicleBodyFinish()) {
  prepareVehicleFinishMaterials(object);
  applyVehicleBodyFinish(object, finish);
  applyMaterialDefaults(object);
  const orientation = new THREE.Group();
  orientation.name = 'body_source_orientation';
  orientation.add(object);

  let { size } = objectBounds(orientation);
  const autoYaw = size.x > size.z ? Math.PI / 2 : 0;
  orientation.rotation.y = autoYaw;
  const rotated = objectBounds(orientation);
  orientation.position.set(-rotated.center.x, -rotated.box.min.y, -rotated.center.z);
  const aligned = objectBounds(orientation);
  const baseScale = targetLength / Math.max(aligned.size.z, .0001);

  const root = new THREE.Group();
  root.name = 'body_source_normalized';
  root.scale.setScalar(baseScale);
  root.add(orientation);
  root.updateMatrixWorld(true);

  return {
    root,
    normalization: {
      autoYawRadians: autoYaw,
      sourceOffset: orientation.position.toArray(),
      baseScale,
      sourceDimensions: rotated.size.toArray(),
      normalizedLength: targetLength,
    },
  };
}

function wheelAxisRotation(axis) {
  if (axis === 'y') return new THREE.Euler(0, 0, Math.PI / 2);
  if (axis === 'z') return new THREE.Euler(0, Math.PI / 2, 0);
  return new THREE.Euler();
}

export function normalizeWheelSource(object) {
  applyMaterialDefaults(object);
  const orientation = new THREE.Group();
  orientation.name = 'wheel_source_orientation';
  orientation.add(object);

  const initial = objectBounds(orientation);
  const entries = [['x', initial.size.x], ['y', initial.size.y], ['z', initial.size.z]];
  entries.sort((a, b) => a[1] - b[1]);
  const sourceAxis = entries[0][0];
  orientation.rotation.copy(wheelAxisRotation(sourceAxis));
  const rotated = objectBounds(orientation);
  orientation.position.copy(rotated.center).multiplyScalar(-1);
  const centered = objectBounds(orientation);
  const sourceRadius = Math.max(centered.size.y, centered.size.z) * .5;
  const baseScale = 1 / Math.max(sourceRadius, .0001);
  const normalizedWidth = centered.size.x * baseScale;

  const root = new THREE.Group();
  root.name = 'wheel_source_normalized';
  root.scale.setScalar(baseScale);
  root.add(orientation);
  root.updateMatrixWorld(true);

  return {
    root,
    normalization: {
      sourceAxis,
      sourceRotationRadians: orientation.rotation.toArray().slice(0, 3),
      sourceOffset: orientation.position.toArray(),
      baseScale,
      sourceRadius,
      normalizedWidth,
      sourceDimensions: centered.size.toArray(),
    },
  };
}

function disposeObject(object) {
  object?.traverse((child) => {
    if (!child.isMesh) return;
    child.geometry?.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.filter(Boolean).forEach((material) => material.dispose?.());
  });
}

export class VehicleAssetRig {
  constructor(profile = createVehicleProfile()) {
    this.profile = sanitizeVehicleProfile(profile);
    this.root = new THREE.Group();
    this.root.name = 'calibrated_vehicle';
    this.bodyMount = new THREE.Group();
    this.bodyMount.name = 'body_mount';
    this.wheelGroup = new THREE.Group();
    this.wheelGroup.name = 'wheel_mounts';
    this.root.add(this.bodyMount, this.wheelGroup);
    this.bodySource = null;
    this.interiorMesh = null;
    this.interiorSignature = '';
    this.wheelSource = null;
    this.wheelInheritsBodyFinish = false;
    this.wheelMounts = {};
  }

  setProfile(profile) {
    this.profile = sanitizeVehicleProfile(profile);
    this.applyProfile();
  }

  setBody(object, fileName = '') {
    if (this.bodySource) disposeObject(this.bodySource);
    this.bodyMount.clear();
    const normalized = normalizeBodySource(object, 4.4, this.profile.body.finish);
    this.bodySource = normalized.root;
    this.bodyMount.add(this.bodySource);
    if (this.interiorMesh) this.bodyMount.add(this.interiorMesh);
    this.profile.assets.bodyFile = fileName;
    this.profile.body.normalization = normalized.normalization;
    this.applyProfile();
    return normalized.normalization;
  }

  setWheel(object, fileName = '', { inheritBodyFinish = false } = {}) {
    if (this.wheelSource) disposeObject(this.wheelSource);
    this.wheelGroup.clear();
    const normalized = normalizeWheelSource(object);
    this.wheelInheritsBodyFinish = Boolean(inheritBodyFinish);
    if (this.wheelInheritsBodyFinish) {
      prepareVehicleFinishMaterials(normalized.root);
      applyVehicleBodyFinish(normalized.root, this.profile.body.finish);
    }
    this.wheelSource = normalized.root;
    this.profile.assets.wheelFile = fileName;
    this.profile.wheels.normalization = normalized.normalization;
    this.wheelMounts = {};
    for (const axle of ['front', 'rear']) {
      for (const side of ['L', 'R']) {
        const name = `wheel_${axle === 'front' ? 'F' : 'R'}${side}`;
        const mount = new THREE.Group();
        mount.name = name;
        mount.add(this.wheelSource.clone(true));
        this.wheelGroup.add(mount);
        this.wheelMounts[name] = mount;
      }
    }
    this.applyProfile();
    return normalized.normalization;
  }

  applyProfile() {
    const profile = this.profile;
    this.updateInteriors();
    applyVehicleBodyFinish(this.bodySource, profile.body.finish);
    if (this.wheelInheritsBodyFinish) {
      applyVehicleBodyFinish(this.wheelSource, profile.body.finish);
    }
    this.bodyMount.position.set(profile.body.offset.x, profile.body.offset.y, profile.body.offset.z);
    this.bodyMount.rotation.set(
      THREE.MathUtils.degToRad(profile.body.rotationDegrees.x),
      THREE.MathUtils.degToRad(profile.body.rotationDegrees.y),
      THREE.MathUtils.degToRad(profile.body.rotationDegrees.z),
    );
    this.bodyMount.scale.setScalar(profile.body.scale);

    const normalizedWidth = profile.wheels.normalization?.normalizedWidth || .48;
    for (const [axleName, axle] of Object.entries({ front: profile.wheels.front, rear: profile.wheels.rear })) {
      for (const side of ['L', 'R']) {
        const mount = this.wheelMounts[`wheel_${axleName === 'front' ? 'F' : 'R'}${side}`];
        if (!mount) continue;
        const sign = side === 'L' ? -1 : 1;
        mount.position.set(sign * axle.track * .5, axle.centerY, axle.axleZ);
        mount.rotation.set(0, side === 'L' && profile.wheels.flipLeft ? Math.PI : 0, 0);
        mount.scale.set(axle.width / Math.max(normalizedWidth, .001), axle.radius, axle.radius);
      }
    }
    this.root.updateMatrixWorld(true);
  }

  snapshot() {
    return sanitizeVehicleProfile(this.profile);
  }

  updateInteriors() {
    const interiors = sanitizeVehicleInteriors(this.profile.body.interiors);
    const signature = JSON.stringify(interiors);
    if (signature === this.interiorSignature) return;
    this.interiorSignature = signature;
    const geometry = interiors.enabled ? createInteriorGeometry(interiors.boxes) : null;
    if (this.interiorMesh) {
      this.interiorMesh.geometry.dispose();
      if (geometry) this.interiorMesh.geometry = geometry;
      else {
        this.interiorMesh.removeFromParent();
        this.interiorMesh.material.dispose();
        this.interiorMesh = null;
      }
    } else if (geometry) {
      this.interiorMesh = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({
        vertexColors: true, roughness: 1, metalness: 0,
      }));
      this.interiorMesh.name = 'vehicle_visual_interiors';
      this.interiorMesh.castShadow = true;
      this.interiorMesh.receiveShadow = true;
      this.bodyMount.add(this.interiorMesh);
    }
  }

  dispose() {
    if (this.interiorMesh) {
      this.interiorMesh.geometry.dispose();
      this.interiorMesh.material.dispose();
      this.interiorMesh.removeFromParent();
      this.interiorMesh = null;
    }
    this.interiorSignature = '';
    if (this.bodySource) disposeObject(this.bodySource);
    if (this.wheelSource) disposeObject(this.wheelSource);
    this.wheelInheritsBodyFinish = false;
    this.root.clear();
  }
}
